import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Layout from '../components/Layout.jsx';
import PageLoader from '../components/common/PageLoader.jsx';

const HomePage = lazy(() => import('../pages/HomePage.jsx'));
const PricingPage = lazy(() => import('../pages/PricingPage.jsx'));
const CitizenDashboard = lazy(() => import('../pages/dashboard/CitizenDashboard.jsx'));
const PoliceDashboard = lazy(() => import('../pages/dashboard/PoliceDashboard.jsx'));
const PhoneOsintPage = lazy(() => import('../pages/dashboard/PhoneOsintPage.jsx'));
const OsintLayout = lazy(() => import('../pages/dashboard/osint/OsintLayout.jsx'));
const OsintCategoriesOverviewPage = lazy(() => import('../pages/dashboard/osint/OsintCategoriesOverviewPage.jsx'));
const CategoryPage = lazy(() => import('../pages/dashboard/osint/CategoryPage.jsx'));
const CasePlaceholderPage = lazy(() => import('../pages/dashboard/osint/CasePlaceholderPage.jsx'));
const CaseDashboardPage = lazy(() => import('../pages/dashboard/osint/CaseDashboardPage.jsx'));
const CaseGroupPage = lazy(() => import('../pages/dashboard/osint/CaseGroupPage.jsx'));
const ForensicsGroupPage = lazy(() => import('../pages/dashboard/osint/ForensicsGroupPage.jsx'));
const ForensicsPlaceholderPage = lazy(() => import('../pages/dashboard/osint/ForensicsPlaceholderPage.jsx'));
const CaseFirRegistrationPage = lazy(() => import('../pages/dashboard/osint/CaseFirRegistrationPage.jsx'));
const CaseChargeSheetsPage = lazy(() => import('../pages/dashboard/osint/CaseChargeSheetsPage.jsx'));
const CaseArrestsPage = lazy(() => import('../pages/dashboard/osint/CaseArrestsPage.jsx'));
const CaseVictimsAccusedWitnessesPage = lazy(() => import('../pages/dashboard/osint/CaseVictimsAccusedWitnessesPage.jsx'));
const CasePropertySeizurePage = lazy(() => import('../pages/dashboard/osint/CasePropertySeizurePage.jsx'));
const CaseEvidenceCocPage = lazy(() => import('../pages/dashboard/osint/CaseEvidenceCocPage.jsx'));
const CaseLogPage = lazy(() => import('../pages/dashboard/osint/CaseLogPage.jsx'));
const CaseInvestigationNotesPage = lazy(() => import('../pages/dashboard/osint/CaseInvestigationNotesPage.jsx'));
const CaseTasksPendingPage = lazy(() => import('../pages/dashboard/osint/CaseTasksPendingPage.jsx'));
const CaseMasterTimelinePage = lazy(() => import('../pages/dashboard/osint/CaseMasterTimelinePage.jsx'));
const CaseDiaryAllFirsPage = lazy(() => import('../pages/dashboard/osint/CaseDiaryAllFirsPage.jsx'));
const CaseHashVerificationPage = lazy(() => import('../pages/dashboard/osint/CaseHashVerificationPage.jsx'));
const CaseCloudEvidenceRequestsPage = lazy(() => import('../pages/dashboard/osint/CaseCloudEvidenceRequestsPage.jsx'));
const CaseMediaRepositoryPage = lazy(() => import('../pages/dashboard/osint/CaseMediaRepositoryPage.jsx'));
const CaseLinkAnalysisPage = lazy(() => import('../pages/dashboard/osint/CaseLinkAnalysisPage.jsx'));
const CasePlatformInvestigationsPage = lazy(() => import('../pages/dashboard/osint/CasePlatformInvestigationsPage.jsx'));
const CaseEmailHeaderAnalyzerPage = lazy(() => import('../pages/dashboard/osint/CaseEmailHeaderAnalyzerPage.jsx'));
const CaseRankHierarchyPage = lazy(() => import('../pages/dashboard/osint/CaseRankHierarchyPage.jsx'));
const CaseHolidayCalendarPage = lazy(() => import('../pages/dashboard/osint/CaseHolidayCalendarPage.jsx'));
const CaseLegalReferencePage = lazy(() => import('../pages/dashboard/osint/CaseLegalReferencePage.jsx'));
const CaseSopsPage = lazy(() => import('../pages/dashboard/osint/CaseSopsPage.jsx'));
const CaseInvestigationToolsRefPage = lazy(() => import('../pages/dashboard/osint/CaseInvestigationToolsRefPage.jsx'));
const CaseIntegrationsPage = lazy(() => import('../pages/dashboard/osint/CaseIntegrationsPage.jsx'));
const CaseOsintToolkitPage = lazy(() => import('../pages/dashboard/osint/CaseOsintToolkitPage.jsx'));
const SuperAdminDashboard = lazy(() => import('../pages/dashboard/SuperAdminDashboard.jsx'));
const SuperAdminLoginPage = lazy(() => import('../pages/auth/SuperAdminLoginPage.jsx'));
const ContactPage = lazy(() => import('../pages/static/ContactPage.jsx'));
const DisclaimerPage = lazy(() => import('../pages/static/DisclaimerPage.jsx'));
const FAQPage = lazy(() => import('../pages/static/FAQPage.jsx'));
const FeedbackPage = lazy(() => import('../pages/static/FeedbackPage.jsx'));
const PrivacyPage = lazy(() => import('../pages/static/PrivacyPage.jsx'));

const AppRoutes = () => (
  <BrowserRouter>
    <Suspense fallback={<PageLoader />}>
      <Routes>

        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="disclaimer" element={<DisclaimerPage />} />
          <Route path="faq" element={<FAQPage />} />
          <Route path="feedback" element={<FeedbackPage />} />
          <Route path="privacy-policy" element={<PrivacyPage />} />
          <Route path="citizen-dashboard" element={<CitizenDashboard />} />
          <Route path="police-dashboard" element={<PoliceDashboard />} />
        </Route>
        {/* Standalone pages — no navbar/footer */}
        <Route path="/admin-login" element={<SuperAdminLoginPage />} />
        <Route path="/super-admin-dashboard" element={<SuperAdminDashboard />} />
        <Route path="/police-osint" element={<OsintLayout />}>
          <Route index element={<CaseDashboardPage />} />
          <Route path="all-tools" element={<OsintCategoriesOverviewPage />} />
          <Route path="group/:groupSlug" element={<CaseGroupPage />} />
          <Route path="forensics/group/:groupSlug" element={<ForensicsGroupPage />} />
          <Route path="forensics/fm/:slug" element={<ForensicsPlaceholderPage />} />
          <Route path="cm/fir-registration" element={<CaseFirRegistrationPage />} />
          <Route path="cm/charge-sheets" element={<CaseChargeSheetsPage />} />
          <Route path="cm/arrests" element={<CaseArrestsPage />} />
          <Route path="cm/victims-accused-witnesses" element={<CaseVictimsAccusedWitnessesPage />} />
          <Route path="cm/property-seizure" element={<CasePropertySeizurePage />} />
          <Route path="cm/evidence-coc" element={<CaseEvidenceCocPage />} />
          <Route path="cm/case-log" element={<CaseLogPage />} />
          <Route path="cm/investigation-notes" element={<CaseInvestigationNotesPage />} />
          <Route path="cm/tasks-pending" element={<CaseTasksPendingPage />} />
          <Route path="cm/timeline" element={<CaseMasterTimelinePage />} />
          <Route path="cm/case-diary" element={<CaseDiaryAllFirsPage />} />
          <Route path="cm/hash-verification" element={<CaseHashVerificationPage />} />
          <Route path="cm/cloud-evidence-requests" element={<CaseCloudEvidenceRequestsPage />} />
          <Route path="cm/media-repository" element={<CaseMediaRepositoryPage />} />
          <Route path="cm/link-analysis" element={<CaseLinkAnalysisPage />} />
          <Route path="cm/social-media-investigation" element={<CasePlatformInvestigationsPage />} />
          <Route path="cm/email-header-analyzer" element={<CaseEmailHeaderAnalyzerPage />} />
          <Route path="cm/rank-hierarchy" element={<CaseRankHierarchyPage />} />
          <Route path="cm/holiday-calendar" element={<CaseHolidayCalendarPage />} />
          <Route path="cm/legal-reference" element={<CaseLegalReferencePage />} />
          <Route path="cm/case-sops" element={<CaseSopsPage />} />
          <Route path="cm/investigation-tools-reference" element={<CaseInvestigationToolsRefPage />} />
          <Route path="cm/integrations" element={<CaseIntegrationsPage />} />
          <Route path="cm/osint-toolkit" element={<CaseOsintToolkitPage />} />
          <Route path="cm/:slug" element={<CasePlaceholderPage />} />
          <Route path=":categoryId" element={<CategoryPage />} />
        </Route>
        <Route path="/phone-osint" element={<PhoneOsintPage />} />
      </Routes>

    </Suspense>
  </BrowserRouter>
);

export default AppRoutes;
